import { useEffect, useState, useMemo } from "react";
import { api, STATUS_OPTIONS, CATEGORY_OPTIONS, formatErrorDetail } from "@/lib/api";
import UpdateCard from "@/components/UpdateCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, FilterX, Loader2, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ALL = "__all__";

export default function PublicFeed() {
  const [updates, setUpdates] = useState([]);
  const [anlagen, setAnlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [anlageId, setAnlageId] = useState(ALL);
  const [statusF, setStatusF] = useState(ALL);
  const [categoryF, setCategoryF] = useState(ALL);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (anlageId !== ALL) params.anlage_id = anlageId;
      if (statusF !== ALL) params.status = statusF;
      if (categoryF !== ALL) params.category = categoryF;
      const [{ data: upd }, { data: an }] = await Promise.all([
        api.get("/updates", { params }),
        api.get("/anlagen"),
      ]);
      setUpdates(upd);
      setAnlagen(an);
    } catch (e) {
      toast.error(formatErrorDetail(e.response?.data?.detail) || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anlageId, statusF, categoryF]);

  const filtered = useMemo(() => {
    if (!query.trim()) return updates;
    const q = query.toLowerCase();
    return updates.filter(
      (u) =>
        u.title.toLowerCase().includes(q) ||
        u.description.toLowerCase().includes(q) ||
        (u.anlagen || []).some((a) => a.name.toLowerCase().includes(q))
    );
  }, [updates, query]);

  const resetFilters = () => {
    setQuery("");
    setAnlageId(ALL);
    setStatusF(ALL);
    setCategoryF(ALL);
  };

  const activeFilterCount = [
    anlageId !== ALL,
    statusF !== ALL,
    categoryF !== ALL,
    query.trim() !== "",
  ].filter(Boolean).length;

  return (
    <div className="min-h-[calc(100vh-5rem)]">
      {/* Hero */}
      <section className="relative border-b border-slate-200 bg-white overflow-hidden">
        <div className="absolute inset-0 bg-grain opacity-60" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#E2001A]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E2001A]/10 text-[#E2001A] text-xs font-semibold uppercase tracking-wider mb-4">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E2001A] animate-pulse" />
              Live-Updates
            </div>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight text-balance"
              data-testid="public-feed-title"
            >
              Updates zu unseren Anlagen
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-2xl text-balance">
              Aktuelle Mitteilungen und Updates für Anlagen
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-slate-200 bg-white sticky top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Updates durchsuchen…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 border-slate-300"
                data-testid="search-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-2 lg:flex lg:items-center">
              <Select value={anlageId} onValueChange={setAnlageId}>
                <SelectTrigger className="border-slate-300" data-testid="filter-anlage">
                  <SelectValue placeholder="Anlage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Alle Anlagen</SelectItem>
                  {anlagen.map((a) => (
                    <SelectItem key={a.id} value={a.id} data-testid={`filter-anlage-option-${a.id}`}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={statusF} onValueChange={setStatusF}>
                <SelectTrigger className="border-slate-300" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Alle Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={categoryF} onValueChange={setCategoryF}>
                <SelectTrigger className="border-slate-300" data-testid="filter-category">
                  <SelectValue placeholder="Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Alle Kategorien</SelectItem>
                  {CATEGORY_OPTIONS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="text-slate-600"
                data-testid="reset-filters-button"
              >
                <FilterX className="h-4 w-4 mr-1" />
                Filter zurücksetzen
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* List */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {loading ? (
          <div className="flex items-center justify-center py-24 text-slate-500" data-testid="feed-loading">
            <Loader2 className="h-6 w-6 mr-2 animate-spin" />
            Updates werden geladen…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24" data-testid="feed-empty">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-slate-100 mb-4">
              <Inbox className="h-8 w-8 text-slate-400" />
            </div>
            <h3 className="font-heading text-xl font-semibold text-slate-900">Keine Updates gefunden</h3>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              {activeFilterCount > 0
                ? "Es passen keine Updates zu den gewählten Filtern."
                : "Sobald Updates veröffentlicht werden, erscheinen sie hier."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="updates-grid">
            {filtered.map((u) => (
              <UpdateCard key={u.id} update={u} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
